/**
 * 🔴🔴 US-AIV2-tourguide-0003 (@UC0361 · TG-INV-3 · QĐ PO Q3) — "TRANG ĐÍCH LÀM ĐƯỢC KHÔNG" có MỘT chủ: binding của trang đích.
 *
 * Trước 0003, câu hỏi này không có chủ: thẻ chỉ hỏi QUYỀN (`ctaReachable` = trang ∈ `allowedPages`), còn "làm được không" rải ở CHỖ
 * PHÁT bằng phép đếm riêng (`myUnpaidDueCount` ≠ tập tự khai được — máy đo K10: thành viên quỹ `admin_one_party` được mời "Báo đã chuyển"
 * rồi bấm ra `DUE_NOT_REPORTABLE`). Nay binding của trang đích khai `ready(ctx, subject?)` bằng ĐÚNG hàm của cửa thật (cam kết #3) và
 * Người dẫn đường chỉ ĐỌC kết quả — domain không tự kiểm (hàm thuần, không DB).
 *
 * Ba kết quả, và hướng fail của mỗi cái:
 *  - `ready`     — làm được; `fill` = chữ điền sẵn cho ô của trang (`fill.<ô>` trên nút — H03), `subject` = đối tượng câu thẻ nói tới;
 *  - `not_ready` — cửa thật nói KHÔNG, kèm lý do ĐÓNG (`AiNotReadyReason`) — Q3: giữ câu thẻ, BỎ nút;
 *  - `unknown`   — không đọc được ⇒ KHÔNG nút (fail-closed): một nút dẫn tới lỗi tệ hơn không có nút.
 *
 * Chính sách dẫn đường (Q6 vừa huỷ · Q7 vừa nhắc · vừa làm xong) KHÔNG nằm ở đây — đó là bộ lọc của `0006`. `ready()` chỉ trả lời điều
 * kiện của CỬA, để kiểm lúc bấm (`0003b`) không chặn một việc người dùng chủ động muốn làm.
 */

/** Loại đối tượng một nút có thể trỏ tới — tập ĐÓNG; id luôn là uuid của bản ghi đó (kiểm `isUuid` + sở hữu Ở `ready`, không ở đây). */
export const AI_READY_SUBJECT_KINDS = ["collection", "memberDue", "claim"] as const;
export type AiReadySubjectKind = (typeof AI_READY_SUBJECT_KINDS)[number];

export interface AiReadySubject {
  readonly kind: AiReadySubjectKind;
  readonly id: string;
}

/**
 * Lý do "chưa làm được" — mã ĐÓNG, ngôn-ngữ-trung-tính. Câu cho người đọc ở `not-ready-text.ts` (`satisfies Record<…>` ⇒ thêm mã mà quên
 * câu là typecheck đỏ). Mỗi mã gộp các nguyên nhân MÀ CỬA THẬT KHÔNG PHÂN BIỆT cho người hỏi — vd `NOTHING_TO_REPORT` gồm cả "đã đóng",
 * "đã báo rồi", "quỹ do người giữ quỹ ghi": cửa tự khai trả MỘT mã cho cả bốn để không lộ suất người khác có khoản hay không.
 */
export const AI_NOT_READY_REASONS = [
  /**
   * PAY-REPORT: không còn khoản nào CHÍNH người này tự báo đã chuyển được ở đợt đang mở — và KHÔNG phân được vì sao (đọc phụ hỏng). Câu là
   * phép tuyển trung thực của mọi nguyên nhân. Khi phân được, `ready()` trả một trong ba mã dưới (QĐ PO 04/10 dòng 42).
   */
  "NOTHING_TO_REPORT",
  /** PAY-REPORT (QĐ PO 04/10 dòng 42): người này CÒN khoản phải đóng nhưng không khoản nào tự báo được (quỹ chỉ người giữ quỹ ghi · khoản ở đợt đã đóng sổ) ⇒ người giữ quỹ ghi nhận. */
  "NOT_SELF_REPORTABLE",
  /** PAY-REPORT (QĐ PO 04/10): không còn khoản phải đóng, và có lời báo CỦA người này đang chờ đối chiếu. */
  "ALREADY_REPORTED",
  /** PAY-REPORT (QĐ PO 04/10): không còn khoản phải đóng, không lời báo nào chờ — không còn gì cần đóng. */
  "NOTHING_OWED",
  /** PAY-CONFIRM-DECIDE: không còn lời báo nào chờ người này đối chiếu. */
  "NOTHING_TO_CONFIRM",
  /**
   * EXP-APPROVE (AI-SO-DRIFT-1012 · @UC0217): không còn khoản chi nào chờ người này QUYẾT (duyệt HOẶC từ chối) — danh sách cửa duyệt rỗng. Khoản
   * chỉ còn phải biểu quyết / quỹ khoá / người ứng là chính mình VẪN từ chối được (lõi `reject()` không xét) ⇒ không phải lý do này (review #1420 HIGH-1).
   */
  "NOTHING_TO_DECIDE",
  /** PAY-CONFIRM-DECIDE: mọi người có lời báo chờ đều có ≥2 lời báo — trang chat không chọn hộ lời báo nào (§4), phải mở màn duyệt. */
  "CLAIM_NEEDS_SCREEN",
  /**
   * DEBT-REMIND: đợt không còn suất nào chưa đóng (hoặc không còn đợt mở). Không đối tượng (AI-MENU-READY-EMPTY @UC0201): không đợt mở nào của ô `dot`
   * còn suất chưa đóng — kể cả nhóm CHƯA có đợt mở nào.
   */
  "NOTHING_TO_REMIND",
  /**
   * DEBT-REMIND: còn suất chưa đóng nhưng không suất nào NHẬN được lời nhắc trong app (suất chưa ai nhận / chưa liên kết). Không đối tượng: chỉ khi màn
   * «Nhắc qua Zalo» cũng rỗng — còn dòng ở màn đó thì trang làm được qua lối Zalo (M04 · QĐ PO Q3 28/09).
   */
  "NO_REACHABLE_MEMBER",
  /**
   * 🔴 AI-MENU-READY-EMPTY (@UC0200 @UC0353) — RECORD-PAID không đối tượng: phép cấp quyền của cửa ghi (`listRecordableDuesForUser`) không còn khoản nào ở
   * đợt ĐANG MỞ (nhóm chưa có đợt mở · mọi khoản đã đóng xong · khoản còn lại đang chờ duyệt lời báo) — một mã cho cả ba vì cửa ghi không phân biệt.
   */
  "NOTHING_TO_RECORD",
  /** COL-EQUAL: nhóm không có quỹ nào mở được đợt thu (không quỹ hoạt động · quỹ không có ai phải đóng · đơn vị không phải VND). */
  "NO_FUND_TO_COLLECT",
  /** ONB-ADD-MEMBERS: nhóm này không thêm tên hộ được trong trợ lý (cửa `createSeats` chỉ mở cho nhóm phụ huynh). */
  "GROUP_KIND_UNSUPPORTED",
  /** DUE-CLAIM-WITHDRAW: không còn lời báo nào CHÍNH người này rút được. */
  "NOTHING_TO_WITHDRAW",
  /**
   * 🔴 AI-MENU-POB-ENTRY (@UC0218 · Jira GOPPY-45) — PAY-ON-BEHALF (nút danh mục, không đối tượng): người này vừa KHÔNG đóng hộ được suất nào (CHÍNH
   * phép đọc nhánh đề nghị — đã gác `payment_status_visibility`, nên nhóm `managers_only` không phân biệt "không ai nợ" với "không được xem"), vừa
   * KHÔNG có lời đề nghị đóng hộ nào trả lời được đang chờ mình. Câu là phép hội của hai sự thật về CHÍNH người hỏi — không lộ ai còn nợ.
   */
  "NOTHING_TO_PAY_ON_BEHALF",
  /** Đối tượng nút trỏ tới không còn (đã đóng · không thuộc nhóm · id không hợp lệ) — một câu chung, không lộ nó có tồn tại hay không. */
  "SUBJECT_GONE",
  /**
   * 🔴 G-4 GUIDE-FROZEN-FUND (@UC0361 @UC9300 · CLAUDE.md §3.8) — PAY-REPORT · PAY-CONFIRM-DECIDE · DEBT-REMIND: mọi việc trang làm được cho người
   * này nằm ở quỹ ĐÓNG BĂNG (`isFundFrozen` — CHÍNH vế cửa ghi ném `FUND_FROZEN`: ô đợt/khoản của trợ lý rỗng `SCOPE_FUND_FROZEN`, cửa duyệt và cửa
   * nhắc 409). Không đường mở băng trong app ⇒ câu nói sự thật, không hứa ai làm gì (khuôn PO-DUYET 0014 dòng 4).
   */
  "FUND_FROZEN",
  /**
   * review #1176 MED-2: nút được dựng ở MỘT NHÓM KHÁC của chính người bấm (phạm vi đã đổi từ lúc nút hiện) — không kiểm/mở việc ở nhóm hiện
   * tại bằng nút của nhóm kia (sẽ nói sai "không còn" về việc của nhóm kia, hoặc mở phiếu sai nhóm). Chỉ ra khi người bấm CÒN là thành viên
   * nhóm dựng nút (cổng quyền xác nhận) — nhóm giả/không thuộc ⇒ bỏ qua, kiểm như cũ.
   */
  "OTHER_GROUP",
] as const;
export type AiNotReadyReason = (typeof AI_NOT_READY_REASONS)[number];

export type AiReadyVerdict =
  | { readonly kind: "ready"; readonly subject: AiReadySubject | null; readonly fill: Readonly<Record<string, string>> }
  | { readonly kind: "not_ready"; readonly reason: AiNotReadyReason }
  | { readonly kind: "unknown" };

export type AiReadyOk = Extract<AiReadyVerdict, { kind: "ready" }>;

/** `ready` không đối tượng, không chữ điền sẵn — trang tự hỏi những gì nó cần. */
export const AI_READY_PLAIN: AiReadyOk = Object.freeze({ kind: "ready", subject: null, fill: Object.freeze({}) });

export function aiNotReady(reason: AiNotReadyReason): AiReadyVerdict {
  return { kind: "not_ready", reason };
}

/** Khoá payload của nút mang mã tham chiếu đối tượng (`<kind>:<id>`) — kiểm lúc bấm (`0003b`) đọc lại và hỏi lại `ready` với nó. */
export const AI_CHIP_SUBJECT_KEY = "subject";

export function encodeReadySubject(subject: AiReadySubject): string {
  return `${subject.kind}:${subject.id}`;
}

/**
 * 🔴 US-AIV2-tourguide-0003b (@UC0363 · security LOW-3 #1169) — ngược của `encodeReadySubject`, cho mã đối tượng CLIENT GỬI LẠI lúc bấm. Rác ⇒
 * `null` (KHÔNG ném — payload từ client). Chỉ kiểm HÌNH (loại ∈ tập đóng, id không rỗng, ≤ 64 ký tự): id KHÔNG được tin — `ready()` kiểm
 * `isUuid` + sở hữu bằng hàm của cửa thật, và tầng api chạy cổng quyền TRƯỚC khi hỏi `ready()`.
 */
export function decodeReadySubject(raw: string | undefined): AiReadySubject | null {
  if (typeof raw !== "string") return null;
  const at = raw.indexOf(":");
  if (at <= 0) return null;
  const kind = raw.slice(0, at);
  const id = raw.slice(at + 1);
  if (!(AI_READY_SUBJECT_KINDS as readonly string[]).includes(kind) || id === "" || id.length > 64) return null;
  return { kind: kind as AiReadySubjectKind, id };
}

/**
 * 🔴 US-AIV2-tourguide-0003b (@UC0363 · spec (g)) — NGUỒN của một nút `run_page` (sự kiện đo `ai.guide.click`). Nút thẻ mang khoá này từ lúc
 * dựng (`mintVerifiedChip`: `card` / `detour` cho thẻ bậc BLOCKED); nút A/B do Người làm gắn `ab`; vắng ⇒ `menu` (danh mục / lấp lời chào).
 * KHÔNG phải quyền gì — chỉ để đếm; client gửi giá trị lạ ⇒ đọc thành `menu`.
 */
export const AI_CHIP_ORIGIN_KEY = "origin";
/**
 * review #1176 MED-2 — khoá payload mang id NHÓM mà nút được dựng trong đó (nhóm hiệu lực của lượt dựng nút). Lúc bấm: khác nhóm của lượt
 * bấm ⇒ cổng quyền xác nhận người bấm còn là thành viên nhóm đó ⇒ `OTHER_GROUP`; không xác nhận được (giả · rác · đã rời) ⇒ BỎ QUA khoá này.
 * Không bao giờ dùng để CHỌN nhóm của phiếu — nhóm của phiếu luôn do cổng quyết từ phạm vi của lượt.
 */
export const AI_CHIP_MINTED_GROUP_KEY = "mintedIn";
export const AI_CHIP_ORIGINS = ["card", "menu", "ab", "detour"] as const;
export type AiChipOrigin = (typeof AI_CHIP_ORIGINS)[number];
export function chipOriginOf(payload: Readonly<Record<string, string>>): AiChipOrigin {
  const raw = payload[AI_CHIP_ORIGIN_KEY];
  return (AI_CHIP_ORIGINS as readonly string[]).includes(raw ?? "") ? (raw as AiChipOrigin) : "menu";
}
